/**
 * @fileoverview Triagem dos cards de um quadro do CRM: a última de cada card
 * (status e progresso), numa consulta só por quadro. Quando chega o aviso da
 * triagem de um card (socket da empresa, com o id do card), relê só ele.
 */

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import useTriageEnabled from "../useTriageEnabled";

const useLeadsTriage = (boardId) => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const enabled = useTriageEnabled();
  // leadId -> { sessionId, status, attentionReason, progress }
  const [porCard, setPorCard] = useState({});
  // Quadro mostrado agora: resposta de outro quadro (depois de trocar) é ignorada.
  const quadroRef = useRef(boardId);
  quadroRef.current = boardId;

  useEffect(() => {
    setPorCard({});
    if (!enabled || !boardId) return undefined;
    let vivo = true;
    api
      .get(`/triage-sessions/by-board/${boardId}`)
      .then(({ data }) => {
        if (!vivo || quadroRef.current !== boardId) return;
        setPorCard(Object.fromEntries((Array.isArray(data) ? data : []).map((t) => [t.leadId, t])));
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [enabled, boardId]);

  const relerCard = useCallback((leadId) => {
    const quadro = quadroRef.current;
    api
      .get(`/triage-sessions/by-lead/${leadId}`)
      .then(({ data }) => {
        if (quadroRef.current !== quadro) return;
        setPorCard((prev) => {
          const novo = { ...prev };
          if (data) {
            novo[leadId] = {
              leadId,
              sessionId: data.sessionId,
              status: data.status,
              attentionReason: data.attentionReason,
              progress: data.progress,
            };
          } else {
            delete novo[leadId];
          }
          return novo;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled || !companyId) return undefined;
    const socket = socketManager.getSocket(companyId);
    const timers = new Map();
    const onTriage = (data) => {
      const leadId = Number(data?.leadId);
      if (!leadId) return;
      // Várias mudanças seguidas da mesma triagem viram uma leitura só.
      clearTimeout(timers.get(leadId));
      timers.set(leadId, setTimeout(() => relerCard(leadId), 400));
    };
    socket.on(`company-${companyId}-triage`, onTriage);
    return () => {
      timers.forEach((t) => clearTimeout(t));
      // disconnect() do socket gerenciado só solta os ouvintes deste uso.
      socket.disconnect();
    };
  }, [enabled, companyId, socketManager, relerCard]);

  return porCard;
};

export default useLeadsTriage;
